<template>
  <div class="job-list">
    <div class="job-list__menu text-right">
      <b-button
        variant="primary"
        :disabled="!form.selected.length"
        @click="confirmDelete = true"
        >Delete</b-button
      >
    </div>

    <div class="job-list__errors">
      <template v-for="(value, key) in form.$errors">
        <error-message
          sign="*"
          :key="value"
          :error="form.$getError(key)"
          class="text-center"
        ></error-message>
      </template>
    </div>

    <b-modal
      v-model="confirmDelete"
      centered
      class="modal-confirmation text-center p-3"
      hide-footer
      static
    >
      <b-img
        slot="modal-header-close"
        :src="require('@/assets/icons/x-b.svg')"
        width="45"
        height="30"
      ></b-img>

      <div
        class="d-flex flex-column justify-content-between align-items-center text-center"
      >
        <h2>Delete tools</h2>

        <b-img
          :src="require('@/assets/images/alert.svg')"
          width="150"
          height="150"
          class="modal-confirmation__icon"
        ></b-img>

        <p class="my-4">
          Are you sure you want to delete the selected tool(s)?
        </p>
        <div class="modal-confirmation__footer">
          <b-button variant="outline-secondary" @click="confirmDelete = false"
            >Cancel</b-button
          >
          <b-button variant="danger" :disabled="form.$busy" @click="onDelete">
            <b-spinner small v-if="form.$busy"></b-spinner>&nbsp;Delete
          </b-button>
        </div>
      </div>
    </b-modal>

    <template v-if="jobs.length > 0">
      <job-item-long
        v-for="job in jobs"
        :key="job.id"
        :job="job"
        @change="toggleTool"
      ></job-item-long>
    </template>
    <template v-else>
      <b-row class="py-5 text-center">
        <b-col
          ><p>
            {{
              form.$busy
                ? 'Please wait while we fetch your tools...'
                : 'You have no tools currently...'
            }}
          </p></b-col
        >
      </b-row>
    </template>
  </div>
</template>
<script>
/**
 * ==================================================================================
 * Job List
 * ==================================================================================
 **/

import { mapState, mapMutations, mapActions } from 'vuex'
import debounce from 'lodash/debounce'
import Form from '@/utils/form'
import JobItemLong from '@/components/cards/JobItemLong'
import ErrorMessage from '@/components/inputs/ErrorMessage'

export default {
  components: {
    JobItemLong,
    ErrorMessage
  },
  computed: {
    ...mapState({
      jobs: state => state.job.list,
      jobsMeta: state => state.job.listMeta
    })
  },
  data: function() {
    return {
      confirmDelete: false,
      form: new Form({
        selected: []
      })
    }
  },
  methods: {
    ...mapActions({
      getJobs: 'job/getJobs',
      deleteJobs: 'job/deleteJobs',
      addAlert: 'alert/addAlert'
    }),
    ...mapMutations({
      clearJobs: 'job/clearJobList'
    }),

    init() {
      this.clearJobs()
      this.fetchJobs()
    },

    async fetchJobs(page = 1) {
      let label = 'Loading tools...'
      if (this.form.$busy) return
      this.form.$busy = true
      this.addAlert({
        label: label
      })

      let params = {
        page: page,
        limit: 5,
        mine: true,
        filters: {
          sorting: '-created_at'
        }
      }

      await this.getJobs(params)
      this.form.$busy = false
      this.$root.$emit('alert-message-remove', label)
    },

    async onDelete() {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      this.addAlert({
        state: true,
        label: 'Removing tool(s)...',
        countdown: true
      })

      await this.deleteJobs({ ids: this.form.selected })
        .then(data => {
          this.reset()

          /* Display unsuccesful items */
          if (data.data && data.data.data && data.data.data.errors) {
            this.onError(data.data.data.errors)
          } else {
            this.addAlert({
              label: 'Your tool(s) has been deleted'
            })
          }
        })
        .catch(error => {
          this.reset(false)

          if (error.response && error.response.status === 400) {
            this.clear()
            this.onError(error.response.data.data)
          } else {
            this.form.$handleError(error)
            this.addAlert({
              state: false,
              label: this.form.$errorMessage
            })
          }
        })
    },

    onError(data) {
      this.form.$errors = data.errors

      let message =
        data.deleted_rows > 0
          ? `${data.deleted_rows} tool(s) has been deleted, see list above for unsuccessful items...`
          : 'No tool has been deleted'

      this.addAlert({
        state: data.deleted_rows > 0,
        label: message
      })
    },

    reset(clear = true) {
      this.form.selected = []
      this.form.$setLoading(false)
      this.confirmDelete = false

      if (clear) {
        this.clear()
      }
    },

    clear() {
      this.init()
      this.scrollToTop()
    },

    toggleTool(id) {
      let exist = this.form.selected.indexOf(id)
      if (exist !== -1) {
        this.form.selected.splice(exist, 1)
      } else {
        this.form.selected.push(id)
      }
    },

    handleScroll(e) {
      if (!this.jobsMeta) return
      const docEl = document.documentElement
      if (docEl.scrollTop + window.innerHeight === docEl.scrollHeight) {
        this.loadMore()
      }
    },

    loadMore() {
      if (this.jobsMeta.current_page < this.jobsMeta.last_page) {
        this.fetchJobs(this.jobsMeta.current_page + 1)
      }
    }
  },
  async created() {
    this.init()

    this.handleDebouncedScroll = debounce(this.handleScroll, 100)
    window.addEventListener('scroll', this.handleDebouncedScroll)
  },

  destroyed() {
    window.removeEventListener('scroll', this.handleDebouncedScroll)
  }
}
</script>
<style scoped lang="scss">
.job-list {
  &__menu {
    button {
      min-height: auto;
      padding: 0.6rem 0.75rem;
      width: 125px;
    }
  }

  &__errors {
    margin-bottom: 30px;
  }

  .job-item-long {
    margin-bottom: 45px;
  }
}
</style>
